import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, map } from "rxjs";
import { environment } from "src/environments/environment";

export type OrganizationBrandingAssetType = "header_horizontal" | "header_icon";

export interface OrganizationBrandingAsset {
    id: number;
    assetType: OrganizationBrandingAssetType;
    versionNumber: number;
    originalFileName: string | null;
    mimeType: string;
    fileSizeBytes: number;
    pixelWidth: number | null;
    pixelHeight: number | null;
    status?: "active" | "inactive";
    createdAt: string | null;
    updatedAt: string | null;
    url?: string;
}

interface BrandingAssetResponse {
    success: boolean;
    assets?: OrganizationBrandingAsset[];
    asset?: OrganizationBrandingAsset;
    message?: string;
}

@Injectable({ providedIn: "root" })
export class OrganizationBrandingService {
    private readonly endpoint = `${environment.baseurl}organization-branding`;
    private readonly brandingChangedSubject = new Subject<void>();

    constructor(private readonly http: HttpClient) {}

    watchBrandingChanges(): Observable<void> {
        return this.brandingChangedSubject.asObservable();
    }

    notifyBrandingChanged(): void {
        this.brandingChangedSubject.next();
    }

    /** The API derives the account from the authenticated session. */
    getActiveAssets(): Observable<OrganizationBrandingAsset[]> {
        return this.http
            .get<BrandingAssetResponse>(`${this.endpoint}/assets`)
            .pipe(map((response) => this.requireAssets(response)));
    }

    getHistory(assetType?: OrganizationBrandingAssetType): Observable<OrganizationBrandingAsset[]> {
        const path = assetType ? `/history/${encodeURIComponent(assetType)}` : "/history";
        return this.http
            .get<BrandingAssetResponse>(`${this.endpoint}${path}`)
            .pipe(map((response) => this.requireAssets(response)));
    }

    uploadAsset(
        assetType: OrganizationBrandingAssetType,
        file: File
    ): Observable<OrganizationBrandingAsset> {
        const formData = new FormData();
        formData.append("file", file, file.name);

        return this.http
            .post<BrandingAssetResponse>(
                `${this.endpoint}/assets/${encodeURIComponent(assetType)}`,
                formData
            )
            .pipe(map((response) => this.requireAsset(response)));
    }

    removeAsset(assetType: OrganizationBrandingAssetType): Observable<OrganizationBrandingAsset> {
        return this.http
            .delete<BrandingAssetResponse>(
                `${this.endpoint}/assets/${encodeURIComponent(assetType)}`
            )
            .pipe(map((response) => this.requireAsset(response)));
    }

    fetchAssetBlob(assetType: OrganizationBrandingAssetType): Observable<Blob> {
        return this.http.get(
            `${this.endpoint}/assets/${encodeURIComponent(assetType)}`,
            { responseType: "blob" }
        );
    }

    private requireAsset(response: BrandingAssetResponse): OrganizationBrandingAsset {
        this.requireSuccess(response);
        if (!response.asset) {
            throw new Error(response.message || "The organization branding response was incomplete.");
        }
        return response.asset;
    }

    private requireAssets(response: BrandingAssetResponse): OrganizationBrandingAsset[] {
        this.requireSuccess(response);
        return response.assets ?? [];
    }

    private requireSuccess(response: BrandingAssetResponse): void {
        if (!response.success) {
            throw new Error(response.message || "Unable to load organization branding.");
        }
    }
}
